import { usePersona } from "@/contexts/PersonaContext";
import { useSiteContent } from "@/contexts/SiteContentContext";
import { HOME } from "@/lib/homeContent";
import { trackEvent } from "@/lib/analytics";

// Public paths (Phase 7.5.3): work · retire · real_estate. Legacy "home" answers resolve to neutral.
export const PROFILES = ["work", "retire", "real_estate"];
export const ROUTE_LABELS = { work: "Work from Spain", retire: "Retire in Spain", real_estate: "Buy a Home" };
const PERSONA_BY_PROFILE = { work: "relocate", retire: "retire", real_estate: null };
// `need` is no longer asked; derived from stage so existing content keys keep working.
const NEED_BY_STAGE = { exploring: "options", considering: "place", planning: "steps" };

export function deriveNeed(stage) {
  return NEED_BY_STAGE[stage] || "start";
}

// Single source of truth: ma_persona_v1 (PersonaContext). Skip → neutral, never inferred.
export function usePersonalization() {
  const { checkin, skipped, checkinDone, updatePersona } = usePersona();
  const { section } = useSiteContent();
  const p = section("personalization", HOME.personalization);
  const profile = PROFILES.includes(checkin?.goal) ? checkin.goal : null;
  const isNeutral = !profile;
  const isRealEstate = profile === "real_estate";
  const stage = checkin?.stage || null;
  const need = checkin?.need || deriveNeed(stage);
  const pick = (key) => (profile ? p[`${profile}_${key}`] : null) || p[`neutral_${key}`] || null;
  const reopen = () => updatePersona({ checkinDone: false });

  // Route selector: switch profile immediately, no Check-in needed. Stage is kept when known.
  const setProfile = (next, placement = "route_selector") => {
    if (!PROFILES.includes(next) || next === profile) return;
    trackEvent(profile ? "route_changed" : "route_selected", { previous_profile: profile || "neutral", new_profile: next, stage: stage || "unknown", placement });
    updatePersona({
      skipped: false,
      checkinDone: true,
      persona: PERSONA_BY_PROFILE[next],
      status: stage,
      country: "Spain",
      checkin: { goal: next, stage, need: deriveNeed(stage) },
    });
  };

  const nextSteps = profile
    ? [
        { text: p[`${profile}_step1`], action: "explore" },
        { text: p[`${profile}_step2_${stage}`] || p[`step2_${need}`] || p.step2_start, action: isRealEstate ? "resources" : need === "place" ? "resources" : "explore" },
        { text: isRealEstate ? p.real_estate_step3 : p.step3_guide, action: isRealEstate ? "how_it_works" : "guide" },
        { text: p[`${profile}_step4_${stage}`] || p[`step4_${stage}`] || p.step4_exploring, action: "call" },
      ]
    : [
        { text: p.neutral_step1, action: "explore" },
        { text: p.neutral_step2, action: "resources" },
        { text: p.step3_guide, action: "guide" },
        { text: p.neutral_step4, action: "call" },
      ];

  return { profile, stage, need, isNeutral, isRealEstate, skipped: !!skipped, checkinDone, pick, p, nextSteps, reopen, setProfile };
}

// Sort resources: matching profile first (personalized) or editorial hierarchy (neutral).
const TYPE_ORDER = { aspirational: 0, lifestyle: 1, decision: 2, practical: 3, legal: 4, general: 5 };
export function orderResources(posts, { profile, need }) {
  const typeRank = (x) => TYPE_ORDER[x.editorial_type] ?? 5;
  if (!profile) return [...posts].sort((a, b) => typeRank(a) - typeRank(b));
  const score = (x) => {
    let s = 0;
    const pt = x.persona_tags || [], nt = x.need_tags || [];
    if (pt.includes(profile)) s -= 4;
    if (pt.includes("general") && profile !== "real_estate") s -= 1;
    if (need && nt.includes(need)) s -= 2;
    return s + typeRank(x) * 0.1;
  };
  return [...posts].sort((a, b) => score(a) - score(b));
}
